import { PRACTICE_QUESTION_SEED } from '../data/practiceQuestionSeed';
import { mergePractice } from './guestProgress';
import { localDateKey, nextStreak, type PracticeQuestion } from './practiceModel';
import { normalizeDiagram } from './roadDiagram';
import { supabase } from './supabaseClient';

const STORAGE_KEY = 'mns-practice-v1';

export type ReportKind = 'wrong_outdated' | 'broken' | 'typo_translation' | 'other';
export type AttemptSource = 'topic' | 'missed' | 'hardest' | 'bookmarked' | 'exam';

export interface StoredAttempt {
  id: string;
  questionId: string;
  pathId: string;
  selectedIndex: number;
  correct: boolean;
  source: AttemptSource;
  createdAt: string;
}

export interface StoredExam {
  id: string;
  pathId: string;
  questionCount: number;
  correctCount: number;
  percent: number;
  passed: boolean;
  createdAt: string;
}

export interface StoredReport {
  id: string;
  userId: string | null;
  pathSlug: string;
  lessonSlug: string | null;
  cardIndex: number | null;
  questionId: string | null;
  kind: ReportKind;
  details: string;
  status: 'open' | 'resolved';
  createdAt: string;
}

export interface StoredStreak {
  current: number;
  longest: number;
  lastDate: string | null;
}

interface Bucket {
  bookmarks: string[];
  attempts: StoredAttempt[];
  exams: StoredExam[];
  streak: StoredStreak;
  reports: StoredReport[];
}

export interface PracticeSnapshot {
  bookmarks: string[];
  attempts: StoredAttempt[];
  exams: StoredExam[];
  streak: StoredStreak;
  source: 'database' | 'device';
}

const emptyStreak = (): StoredStreak => ({ current: 0, longest: 0, lastDate: null });

const emptyBucket = (): Bucket => ({
  bookmarks: [],
  attempts: [],
  exams: [],
  streak: emptyStreak(),
  reports: [],
});

function bucketKey(userId: string | null): string {
  return userId || 'device';
}

function readAll(): Record<string, Bucket> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, Bucket>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(data: Record<string, Bucket>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function readBucket(userId: string | null): Bucket {
  const bucket = readAll()[bucketKey(userId)];
  if (!bucket) return emptyBucket();
  return {
    bookmarks: Array.isArray(bucket.bookmarks) ? bucket.bookmarks : [],
    attempts: Array.isArray(bucket.attempts) ? bucket.attempts : [],
    exams: Array.isArray(bucket.exams) ? bucket.exams : [],
    streak: bucket.streak || emptyStreak(),
    reports: Array.isArray(bucket.reports) ? bucket.reports : [],
  };
}

function writeBucket(userId: string | null, bucket: Bucket): void {
  const all = readAll();
  all[bucketKey(userId)] = bucket;
  writeAll(all);
}

function missingTable(message: string): boolean {
  return /schema cache|does not exist|could not find|PGRST|relation/i.test(message);
}

export function normalizeQuestion(row: Record<string, unknown>): PracticeQuestion {
  const choicesRaw = Array.isArray(row.choices) ? row.choices : [];
  return {
    id: String(row.id),
    path_id: String(row.path_id),
    topic: String(row.topic || 'general'),
    prompt_en: String(row.prompt_en || ''),
    prompt_my: String(row.prompt_my || ''),
    choices: choicesRaw.map((choice) => {
      if (typeof choice === 'string') return { text_en: choice, text_my: '' };
      const item = choice as { text_en?: string; text_my?: string };
      return { text_en: String(item?.text_en || ''), text_my: String(item?.text_my || '') };
    }),
    correct_index: Number(row.correct_index ?? 0),
    explanation_en: String(row.explanation_en || ''),
    explanation_my: String(row.explanation_my || ''),
    tip_en: String(row.tip_en || ''),
    tip_my: String(row.tip_my || ''),
    image_url: row.image_url ? String(row.image_url) : null,
    diagram: normalizeDiagram(row.diagram),
    caption_en: String(row.caption_en || ''),
    caption_my: String(row.caption_my || ''),
    status: row.status === 'draft' ? 'draft' : 'published',
    sort_order: Number(row.sort_order || 0),
  };
}

export async function loadQuestions(pathId: string): Promise<{ questions: PracticeQuestion[]; source: 'database' | 'seed' | 'empty' }> {
  const { data, error } = await supabase
    .from('learning_questions')
    .select('*')
    .eq('path_id', pathId)
    .eq('status', 'published')
    .order('sort_order');
  if (error) {
    return {
      questions: PRACTICE_QUESTION_SEED.filter((question) => question.path_id === pathId && question.status === 'published'),
      source: 'seed',
    };
  }
  const questions = (data || []).map((row) => normalizeQuestion(row as unknown as Record<string, unknown>));
  if (questions.length === 0) return { questions: [], source: 'empty' };
  return { questions, source: 'database' };
}

export async function loadAllQuestions(): Promise<{ questions: PracticeQuestion[]; source: 'database' | 'seed' | 'empty' }> {
  const { data, error } = await supabase.from('learning_questions').select('*').order('sort_order');
  if (error) {
    return { questions: PRACTICE_QUESTION_SEED.filter((question) => question.status === 'published'), source: 'seed' };
  }
  const questions = (data || []).map((row) => normalizeQuestion(row as unknown as Record<string, unknown>));
  if (questions.length === 0) return { questions: [], source: 'empty' };
  return { questions, source: 'database' };
}

async function readRemote(userId: string | null): Promise<PracticeSnapshot | null> {
  if (!userId) return null;
  const [bookmarks, attempts, exams, streak] = await Promise.all([
    supabase.from('question_bookmarks').select('question_id, created_at').eq('user_id', userId),
    supabase.from('question_attempts').select('id, question_id, path_id, selected_index, correct, source, created_at').eq('user_id', userId),
    supabase.from('exam_attempts').select('id, path_id, question_count, correct_count, percent, passed, created_at').eq('user_id', userId),
    supabase.from('study_streaks').select('current_streak, longest_streak, last_study_date').eq('user_id', userId).maybeSingle(),
  ]);
  if (bookmarks.error || attempts.error || exams.error || streak.error) return null;
  return {
    bookmarks: (bookmarks.data || []).map((row) => String(row.question_id)),
    attempts: (attempts.data || []).map((row) => ({
      id: String(row.id),
      questionId: String(row.question_id),
      pathId: String(row.path_id || ''),
      selectedIndex: Number(row.selected_index),
      correct: Boolean(row.correct),
      source: row.source as AttemptSource,
      createdAt: String(row.created_at),
    })),
    exams: (exams.data || []).map((row) => ({
      id: String(row.id),
      pathId: String(row.path_id),
      questionCount: Number(row.question_count),
      correctCount: Number(row.correct_count),
      percent: Number(row.percent),
      passed: Boolean(row.passed),
      createdAt: String(row.created_at),
    })),
    streak: {
      current: Number(streak.data?.current_streak || 0),
      longest: Number(streak.data?.longest_streak || 0),
      lastDate: streak.data?.last_study_date ? String(streak.data.last_study_date) : null,
    },
    source: 'database',
  };
}

function snapshotFromBucket(bucket: Bucket): PracticeSnapshot {
  return {
    bookmarks: bucket.bookmarks,
    attempts: bucket.attempts,
    exams: bucket.exams,
    streak: bucket.streak,
    source: 'device',
  };
}

export function deviceHasExam(): boolean {
  return readBucket(null).exams.length > 0;
}

export async function loadPracticeSnapshot(userId: string | null): Promise<PracticeSnapshot> {
  const remote = await readRemote(userId);
  const own = snapshotFromBucket(readBucket(userId));
  const device = userId ? snapshotFromBucket(readBucket(null)) : own;
  const local = userId ? mergePractice(device, own) : own;
  if (!remote) return { ...local, source: 'device' };
  const remoteHasRows = remote.bookmarks.length + remote.attempts.length + remote.exams.length > 0 || remote.streak.current > 0;
  const localHasRows = local.attempts.length + local.exams.length + local.bookmarks.length > 0 || local.streak.current > 0;
  if (!remoteHasRows && localHasRows) return { ...local, source: 'device' };
  const merged = mergePractice(local, remote);
  return { ...merged, source: 'database' };
}

/** Writes the lesson-finish streak. Practice still uses touchStreak and nextStreak. */
export async function setStudyStreak(userId: string | null, streak: StoredStreak): Promise<void> {
  const bucket = readBucket(userId);
  const next: StoredStreak = {
    current: Math.max(0, Math.floor(streak.current) || 0),
    longest: Math.max(0, Math.floor(streak.longest) || 0),
    lastDate: streak.lastDate,
  };
  writeBucket(userId, { ...bucket, streak: next });
  if (!userId) return;
  const { error } = await supabase.from('study_streaks').upsert({
    user_id: userId,
    current_streak: next.current,
    longest_streak: next.longest,
    last_study_date: next.lastDate,
  });
  if (error && !missingTable(error.message)) {
    console.warn('Streak was saved on this device.', error.message);
  }
}

export async function touchStreak(userId: string | null, today = localDateKey(new Date())): Promise<StoredStreak> {
  const bucket = readBucket(userId);
  const remote = userId ? await readRemote(userId) : null;
  const base = remote?.streak || bucket.streak;
  const next = nextStreak(base.current, base.lastDate, today);
  const streak: StoredStreak = next.changed
    ? {
        current: next.current,
        longest: Math.max(base.longest, next.current),
        lastDate: today,
      }
    : base;
  writeBucket(userId, { ...bucket, streak });
  if (userId && next.changed) {
    const { error } = await supabase.from('study_streaks').upsert({
      user_id: userId,
      current_streak: streak.current,
      longest_streak: streak.longest,
      last_study_date: streak.lastDate,
    });
    if (error && !missingTable(error.message)) {
      console.warn('Streak was saved on this device.', error.message);
    }
  }
  return streak;
}

export async function recordAttempt(
  userId: string | null,
  attempt: Omit<StoredAttempt, 'id' | 'createdAt'>
): Promise<void> {
  await touchStreak(userId);
  const row: StoredAttempt = { ...attempt, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
  const bucket = readBucket(userId);
  writeBucket(userId, { ...bucket, attempts: [...bucket.attempts, row] });
  if (!userId) return;
  const { error } = await supabase.from('question_attempts').insert({
    id: row.id,
    user_id: userId,
    question_id: row.questionId,
    path_id: row.pathId,
    selected_index: row.selectedIndex,
    correct: row.correct,
    source: row.source,
  });
  if (error && !missingTable(error.message)) console.warn('Attempt was saved on this device.', error.message);
}

export async function recordExam(userId: string | null, exam: Omit<StoredExam, 'id' | 'createdAt'>): Promise<void> {
  await touchStreak(userId);
  const row: StoredExam = { ...exam, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
  const bucket = readBucket(userId);
  writeBucket(userId, { ...bucket, exams: [...bucket.exams, row] });
  if (!userId) return;
  const { error } = await supabase.from('exam_attempts').insert({
    id: row.id,
    user_id: userId,
    path_id: row.pathId,
    question_count: row.questionCount,
    correct_count: row.correctCount,
    percent: row.percent,
    passed: row.passed,
  });
  if (error && !missingTable(error.message)) console.warn('Practice exam was saved on this device.', error.message);
}

export async function toggleBookmark(userId: string | null, questionId: string): Promise<boolean> {
  const bucket = readBucket(userId);
  const exists = bucket.bookmarks.includes(questionId);
  const bookmarks = exists ? bucket.bookmarks.filter((id) => id !== questionId) : [...bucket.bookmarks, questionId];
  writeBucket(userId, { ...bucket, bookmarks });
  if (userId) {
    const query = exists
      ? supabase.from('question_bookmarks').delete().eq('user_id', userId).eq('question_id', questionId)
      : supabase.from('question_bookmarks').insert({ user_id: userId, question_id: questionId });
    const { error } = await query;
    if (error && !missingTable(error.message)) console.warn('Bookmark was saved on this device.', error.message);
  }
  return !exists;
}

export async function submitReport(
  userId: string | null,
  input: Omit<StoredReport, 'id' | 'userId' | 'status' | 'createdAt'>
): Promise<{ where: 'database' | 'device' }> {
  const row: StoredReport = {
    ...input,
    id: crypto.randomUUID(),
    userId,
    status: 'open',
    createdAt: new Date().toISOString(),
  };
  const bucket = readBucket(userId);
  writeBucket(userId, { ...bucket, reports: [row, ...bucket.reports] });
  if (!userId) return { where: 'device' };
  const { error } = await supabase.from('content_reports').insert({
    id: row.id,
    user_id: userId,
    path_slug: row.pathSlug,
    lesson_slug: row.lessonSlug,
    card_index: row.cardIndex,
    question_id: row.questionId,
    kind: row.kind,
    details: row.details,
    status: 'open',
  });
  if (error) return { where: 'device' };
  return { where: 'database' };
}

function mapReport(row: Record<string, unknown>): StoredReport {
  return {
    id: String(row.id),
    userId: row.user_id ? String(row.user_id) : null,
    pathSlug: String(row.path_slug || ''),
    lessonSlug: row.lesson_slug ? String(row.lesson_slug) : null,
    cardIndex: row.card_index == null ? null : Number(row.card_index),
    questionId: row.question_id ? String(row.question_id) : null,
    kind: (row.kind as ReportKind) || 'other',
    details: String(row.details || ''),
    status: row.status === 'resolved' ? 'resolved' : 'open',
    createdAt: String(row.created_at || ''),
  };
}

export async function loadReports(): Promise<{ reports: StoredReport[]; source: 'database' | 'device' }> {
  const { data, error } = await supabase.from('content_reports').select('*').order('created_at', { ascending: false });
  const local = Object.values(readAll()).flatMap((bucket) => bucket.reports || []);
  if (error) return { reports: local, source: 'device' };
  const remote = (data || []).map((row) => mapReport(row as unknown as Record<string, unknown>));
  if (remote.length === 0 && local.length > 0) return { reports: local, source: 'device' };
  return { reports: remote, source: 'database' };
}

/** Copies device practice onto the signed-in user without adding the two streaks together. */
export async function adoptGuestPractice(userId: string): Promise<{ attempts: number; exams: number; bookmarks: number }> {
  if (!userId) return { attempts: 0, exams: 0, bookmarks: 0 };
  const all = readAll();
  const device = all.device || emptyBucket();
  const account = all[userId] || emptyBucket();
  const merged = mergePractice(device, account);
  const reports = [...account.reports];
  const seen = new Set(reports.map((report) => report.id));
  for (const report of device.reports) {
    if (report.id && !seen.has(report.id)) reports.push({ ...report, userId });
  }
  all[userId] = { ...merged, reports };
  writeAll(all);

  for (const attempt of device.attempts) {
    const { error } = await supabase.from('question_attempts').insert({
      id: attempt.id,
      user_id: userId,
      question_id: attempt.questionId,
      path_id: attempt.pathId,
      selected_index: attempt.selectedIndex,
      correct: attempt.correct,
      source: attempt.source,
    });
    if (error && !/duplicate|23505/i.test(error.message) && !missingTable(error.message)) {
      console.warn('A guest attempt stayed on this device.', error.message);
      break;
    }
  }
  for (const exam of device.exams) {
    const { error } = await supabase.from('exam_attempts').insert({
      id: exam.id,
      user_id: userId,
      path_id: exam.pathId,
      question_count: exam.questionCount,
      correct_count: exam.correctCount,
      percent: exam.percent,
      passed: exam.passed,
    });
    if (error && !/duplicate|23505/i.test(error.message) && !missingTable(error.message)) {
      console.warn('A guest practice exam stayed on this device.', error.message);
      break;
    }
  }
  for (const questionId of device.bookmarks) {
    const { error } = await supabase.from('question_bookmarks').insert({
      user_id: userId,
      question_id: questionId,
    });
    if (error && !/duplicate|23505/i.test(error.message) && !missingTable(error.message)) {
      console.warn('A guest bookmark stayed on this device.', error.message);
      break;
    }
  }
  await setStudyStreak(userId, merged.streak);
  return {
    attempts: device.attempts.length,
    exams: device.exams.length,
    bookmarks: device.bookmarks.length,
  };
}

export async function resolveReport(id: string): Promise<'database' | 'device'> {
  const { error } = await supabase.from('content_reports').update({ status: 'resolved' }).eq('id', id);
  const all = readAll();
  let localHit = false;
  for (const bucket of Object.values(all)) {
    for (const report of bucket.reports || []) {
      if (report.id === id) {
        report.status = 'resolved';
        localHit = true;
      }
    }
  }
  if (localHit) writeAll(all);
  if (error) return 'device';
  return 'database';
}
